import type { AirportPref, AirportPrefInput } from '../../../src/types.ts';

/** Provider payloads are loosely shaped; read them as records. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Raw = Record<string, any>;

export interface AirportOption {
  /** Unique key used as the Select value. */
  key: string;
  /** Primary IATA code (first code for a city group). */
  code: string;
  /** All codes covered by this option (1 for an airport, 2+ for a city group). */
  codes: string[];
  name: string;
  city: string;
  country: string;
  isCity: boolean;
}

// Some etravel.ly responses return localized text as `{nameEn, nameAr}` (or
// similar) rather than a plain string. Normalize to a single string so we
// don't render `[object Object]`.
export function pickText(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'object') {
    const o = v as Raw;
    return o.nameEn || o.name_en || o.en || o.name || o.label || o.text || o.title || o.code || o.iataCode || '';
  }
  return String(v);
}

export function rawAirportList(data: unknown): Raw[] {
  if (Array.isArray(data)) return data as Raw[];
  const content = (data as Raw | null)?.content;
  return Array.isArray(content) ? (content as Raw[]) : [];
}

export function toAirportOption(a: Raw): AirportOption | null {
  const code = pickText(a.code || a.iataCode || a.id);
  if (!code) return null;
  return {
    key: code,
    code,
    codes: [code],
    name: pickText(a.name || a.airportName || a.fullName) || code,
    city: pickText(a.city || a.cityName),
    country: pickText(a.country || a.countryName || a.countryCode),
    isCity: false,
  };
}

/**
 * Turn a raw airport search response into Select options. When two or more
 * airports share a city, a synthetic "All <city> airports" option is added
 * first so the user can search every airport in that city at once.
 */
export function buildAirportOptions(data: unknown): AirportOption[] {
  const airports = rawAirportList(data)
    .slice(0, 60)
    .map(toAirportOption)
    .filter((a): a is AirportOption => a !== null);

  const byCity = new Map<string, AirportOption[]>();
  for (const a of airports) {
    if (!a.city) continue;
    const k = `${a.city}|${a.country}`;
    const group = byCity.get(k);
    if (group) group.push(a);
    else byCity.set(k, [a]);
  }

  const cityEntries: AirportOption[] = [];
  for (const group of byCity.values()) {
    if (group.length < 2) continue;
    const first = group[0]!;
    const codes = group.map((a) => a.code);
    cityEntries.push({
      key: `city:${first.city}|${first.country}`,
      code: codes[0]!,
      codes,
      name: `All ${first.city} airports`,
      city: first.city,
      country: first.country,
      isCity: true,
    });
  }

  return [...cityEntries, ...airports].slice(0, 40);
}

/** Turn a remembered airport from the server back into a Select option. */
export function fromPref(p: AirportPref): AirportOption {
  return {
    key: p.key || p.code,
    code: p.code,
    codes: p.codes?.length ? p.codes : [p.code],
    name: p.name || p.city || p.code,
    city: p.city ?? '',
    country: p.country ?? '',
    isCity: Boolean(p.isCity),
  };
}

/** What we send the server so it can remember this airport for next time. */
export function toPrefInput(a: AirportOption): AirportPrefInput {
  return { key: a.key, code: a.code, codes: a.codes, name: a.name, city: a.city, country: a.country, isCity: a.isCity };
}

/** Short label for headers: "MJI" or "Tokyo (NRT/HND)". */
export function airportLabel(a: AirportOption): string {
  return a.codes.length > 1 ? `${a.city || a.code} (${a.codes.join('/')})` : a.code;
}
